import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, catchError, throwError, finalize, EMPTY } from 'rxjs';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { LoadingService } from './loading.service';
import { CustomToastrService } from './toastr.service';
import { TokenService } from './token.service';
import { IDSAuthService } from './idsauth.service';
import { UserProfileService } from '../modules/usermanagement/services/user-profile.service';

@Injectable()
export class JWTInterceptor implements HttpInterceptor {
  private isSessionExpiredHandled = false;

  constructor(
    private router: Router,
    private spinner: NgxSpinnerService,
    private loadingService: LoadingService,
    private toastr: CustomToastrService,
    private tokenService: TokenService,
    private idsAuthService: IDSAuthService,
    private userProfileService: UserProfileService
  ) { }

  private handleAuthError(err: HttpErrorResponse): Observable<any> {
    // Check for backend down scenario first (Unknown Error)
    if (err.statusText && err.statusText === 'Unknown Error') {
      console.error('Backend API is down or unreachable');
      this.hideLoadingStates();
      this.router.navigate(['/backend-down']);
      // Return EMPTY to stop error propagation and prevent auto-redirect
      // User must manually click "Retry Connection" to attempt reconnection
      return EMPTY;
    }

    // Handle 401 Unauthorized - Always trigger logout (matching header.component.ts logout logic)
    if (err.status === 401) {
      // Prevent multiple simultaneous logout calls
      if (!this.isSessionExpiredHandled) {
        this.isSessionExpiredHandled = true;

        // Always perform logout on 401, regardless of token validity
        this.toastr.warning("You are not authorized to access this resource. Please log in again.", "Session Expired");
        this.performLogout();

        // Reset the flag after a short delay
        setTimeout(() => {
          this.isSessionExpiredHandled = false;
        }, 2000);
      }
    } else if (err.status === 403) {
      // Permission denied but session might still be valid
      //this.toastr.warning("You don't have permission to access this page or perform this action.", "Access Denied");
    }

    this.hideLoadingStates();
    return throwError(err);
  }

  private performLogout(): void {
    try {
      // Clear user profile from service and local storage
      this.userProfileService.clearUserProfile();

      // IDS signout will clear the identity server session globally
      this.idsAuthService.endSignoutMainWindow().then(() => {
        // Clear localStorage after IDS logout completes
        localStorage.clear();
        this.idsAuthService.userDetails = null;
      }).catch((error) => {
        console.error('Error during IDS signout:', error);
        localStorage.clear();
        window.location.href = '/login';
      });
    } catch (error) {
      console.error('Error during logout:', error);
      localStorage.clear();
      window.location.href = '/login';
    }
  }

  private hideLoadingStates(): void {
    try {
      this.spinner.hide();
      this.loadingService.hide();
    } catch (error) {
      console.warn('Error hiding loading states:', error);
    }
  }
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipAuth = req.headers.has('X-Skip-Auth');
    const skipLoader = req.headers.has('X-Skip-Loader');
    
    if (!skipLoader) {
      this.loadingService.show();
    }
    
    // If skipping auth, just pass through
    if (skipAuth) {
      return next.handle(req).pipe(
        finalize(() => {
          if (!skipLoader) {
            this.loadingService.hide();
          }
        })
      );
    }
    
    // Get token and attach to request
    const token = this.tokenService.getToken();
    const authenticatedReq = token 
      ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) })
      : req;

    return next.handle(authenticatedReq).pipe(
      catchError(x => this.handleAuthError(x)),
      finalize(() => {
        if (!skipLoader) {
          this.loadingService.hide();
        }
      })
    );
  }

}